import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, where, getDocs, onSnapshot, orderBy, limit, Timestamp, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { createNotification } from './notifications';

function SiteAnalytics({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    onlineUsers: [],
    totalPageViews: 0,
    totalSessions: 0,
    popularPages: [],
    recentActivity: [],
    deviceBreakdown: { mobile: 0, desktop: 0 },
    activeToday: 0,
    avgSessionDuration: 0,
    playerActivity: []
  });
  const [dailyActiveUsers, setDailyActiveUsers] = useState([]);
  const [weeklyUniqueCount, setWeeklyUniqueCount] = useState(0);
  const [roundStats, setRoundStats] = useState(null);
  const [activeSeasonName, setActiveSeasonName] = useState('');
  const [roundsModalStatus, setRoundsModalStatus] = useState(null);
  const [roundsList, setRoundsList] = useState([]);
  const [roundsListLoading, setRoundsListLoading] = useState(false);
  const [selectedRound, setSelectedRound] = useState(null);
  const [users, setUsers] = useState({});
  const [allRounds, setAllRounds] = useState([]);
  const [markerStats, setMarkerStats] = useState([]);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeInput, setDisputeInput] = useState('');

  useEffect(() => {
    setShowDisputeForm(false);
    setDisputeInput('');
  }, [selectedRound?.id]);

  useEffect(() => {
    loadAnalytics();

    // Real-time listener for online users
    const presenceQuery = query(
      collection(db, 'analytics_presence'),
      where('isOnline', '==', true)
    );

    const unsubscribe = onSnapshot(presenceQuery, (snapshot) => {
      const onlineUsers = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      setStats(prev => ({ ...prev, onlineUsers }));
    });

    return () => unsubscribe();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);

      // Fetch active season (needed for round stats)
      let activeSeasonId = null;
      const seasonsSnapshot = await getDocs(collection(db, 'seasons'));
      const activeSeasonDoc = seasonsSnapshot.docs.find(doc => doc.data().isActive);
      if (activeSeasonDoc) {
        activeSeasonId = activeSeasonDoc.id;
        setActiveSeasonName(activeSeasonDoc.data().name || '');
      }

      // Get all page views
      const pageViewsSnapshot = await getDocs(collection(db, 'analytics_pageviews'));
      const totalPageViews = pageViewsSnapshot.size;

      // Count page popularity
      const pageCounts = {};
      let mobileCount = 0;
      let desktopCount = 0;

      pageViewsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        pageCounts[data.pageName] = (pageCounts[data.pageName] || 0) + 1;

        if (data.isMobile) {
          mobileCount++;
        } else {
          desktopCount++;
        }
      });

      const popularPages = Object.entries(pageCounts)
        .map(([page, count]) => ({ page, count }))
        .sort((a, b) => b.count - a.count);

      // Fetch player names from users collection
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const userNames = {};
      const usersMap = {};
      usersSnapshot.docs.forEach(doc => {
        userNames[doc.id] = doc.data().name || doc.id;
        usersMap[doc.id] = { name: doc.data().name || 'Unknown', photoURL: doc.data().photoURL || null };
      });
      setUsers(usersMap);

      // Build per-player page stats
      const playerPageStats = {};
      pageViewsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const uid = data.userId;
        if (!uid) return;
        if (!playerPageStats[uid]) {
          playerPageStats[uid] = { userId: uid, name: userNames[uid] || uid, total: 0, pages: {}, lastActive: null };
        }
        playerPageStats[uid].total++;
        playerPageStats[uid].pages[data.pageName] = (playerPageStats[uid].pages[data.pageName] || 0) + 1;
        if (data.timestamp && (!playerPageStats[uid].lastActive || data.timestamp.seconds > playerPageStats[uid].lastActive.seconds)) {
          playerPageStats[uid].lastActive = data.timestamp;
        }
      });

      const playerActivity = Object.values(playerPageStats)
        .map(player => ({
          ...player,
          topPages: Object.entries(player.pages)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 4)
            .map(([page, count]) => ({ page, count }))
        }))
        .sort((a, b) => b.total - a.total);

      // Compute 7-day daily active users from existing pageViewsSnapshot (no extra query)
      const dauBuckets = {};
      const nowDau = new Date();
      for (let i = 6; i >= 0; i--) {
        const d = new Date(nowDau);
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() - i);
        const key = d.toDateString();
        dauBuckets[key] = { date: new Date(d), label: d.toLocaleDateString('en-US', { weekday: 'short' }), users: new Set() };
      }
      pageViewsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.timestamp) return;
        const dateStr = data.timestamp.toDate().toDateString();
        if (dauBuckets[dateStr]) {
          dauBuckets[dateStr].users.add(data.userId);
        }
      });
      const last7Days = Object.values(dauBuckets).map(d => ({
        date: d.date,
        label: d.label,
        count: d.users.size
      }));
      setDailyActiveUsers(last7Days);

      const weeklyAllUsers = new Set();
      Object.values(dauBuckets).forEach(d => d.users.forEach(uid => weeklyAllUsers.add(uid)));
      setWeeklyUniqueCount(weeklyAllUsers.size);

      // Round submission stats for active season
      if (activeSeasonId) {
        const roundsQuery = query(collection(db, 'rounds'), where('seasonId', '==', activeSeasonId));
        const roundsSnapshot = await getDocs(roundsQuery);
        const allSeasonRounds = roundsSnapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(r => !r.isFinale);
        let approved = 0, pending = 0, disputed = 0;
        allSeasonRounds.forEach(round => {
          const status = round.status;
          if (status === 'approved') approved++;
          else if (status === 'pending') pending++;
          else if (status === 'disputed') disputed++;
        });
        const total = allSeasonRounds.length;
        const approvalRate = total > 0 ? Math.round((approved / total) * 100) : 0;
        setRoundStats({ total, approved, pending, disputed, approvalRate });

        // Store all rounds for drill-down
        setAllRounds(allSeasonRounds);

        // Compute marker performance stats
        const markerStatsMap = {};
        allSeasonRounds.forEach(round => {
          const mid = round.markerId;
          if (!mid) return;
          if (!markerStatsMap[mid]) {
            markerStatsMap[mid] = {
              markerId: mid,
              total: 0, approved: 0, pending: 0, disputed: 0,
              totalApprovalHours: 0, approvedWithTime: 0,
            };
          }
          markerStatsMap[mid].total++;
          if (round.status === 'approved') {
            markerStatsMap[mid].approved++;
            if (round.createdAt && round.approvedAt) {
              const submitted = round.createdAt.toDate
                ? round.createdAt.toDate()
                : new Date(round.createdAt);
              const approvedDate = new Date(round.approvedAt);
              const hours = (approvedDate - submitted) / (1000 * 60 * 60);
              if (hours >= 0 && hours < 200) {
                markerStatsMap[mid].totalApprovalHours += hours;
                markerStatsMap[mid].approvedWithTime++;
              }
            }
          } else if (round.status === 'pending') {
            markerStatsMap[mid].pending++;
          } else if (round.status === 'disputed') {
            markerStatsMap[mid].disputed++;
          }
        });

        const markerList = Object.values(markerStatsMap).map(m => ({
          ...m,
          name: usersMap[m.markerId]?.name || 'Unknown',
          avgApprovalHours: m.approvedWithTime > 0
            ? (m.totalApprovalHours / m.approvedWithTime).toFixed(1)
            : null,
          pendingRate: m.total > 0 ? Math.round((m.pending / m.total) * 100) : 0,
        })).sort((a, b) => b.pending - a.pending);

        setMarkerStats(markerList);
      }

      // Get sessions
      const sessionsSnapshot = await getDocs(collection(db, 'analytics_sessions'));
      const totalSessions = sessionsSnapshot.size;

      // Calculate average session duration
      let totalDuration = 0;
      let completedSessions = 0;

      sessionsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.startTime && data.endTime) {
          const duration = data.endTime.seconds - data.startTime.seconds;
          totalDuration += duration;
          completedSessions++;
        }
      });

      const avgSessionDuration = completedSessions > 0
        ? Math.round(totalDuration / completedSessions / 60) // Convert to minutes
        : 0;

      // Get users active today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = Timestamp.fromDate(today);

      const todayQuery = query(
        collection(db, 'analytics_pageviews'),
        where('timestamp', '>=', todayTimestamp)
      );

      const todaySnapshot = await getDocs(todayQuery);
      const uniqueUsersToday = new Set(todaySnapshot.docs.map(doc => doc.data().userId));
      const activeToday = uniqueUsersToday.size;

      // Get recent activity
      const recentQuery = query(
        collection(db, 'analytics_pageviews'),
        orderBy('timestamp', 'desc'),
        limit(20)
      );

      const recentSnapshot = await getDocs(recentQuery);
      const recentActivity = recentSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      setStats({
        onlineUsers: stats.onlineUsers, // Keep real-time data
        totalPageViews,
        totalSessions,
        popularPages,
        recentActivity,
        deviceBreakdown: { mobile: mobileCount, desktop: desktopCount },
        activeToday,
        avgSessionDuration,
        playerActivity
      });

    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminApprove = async (round) => {
    try {
      await updateDoc(doc(db, 'rounds', round.id), {
        status: 'approved',
        approvedAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      });

      await createNotification(round.userId, 'score_approved', { roundId: round.id, date: round.date });

      setSelectedRound(prev => ({ ...prev, status: 'approved' }));
      setAllRounds(prev => prev.map(r =>
        r.id === round.id ? { ...r, status: 'approved' } : r
      ));
      setRoundsList(prev => prev.map(r =>
        r.id === round.id ? { ...r, status: 'approved' } : r
      ));
      setRoundStats(prev => prev ? {
        ...prev,
        approved: prev.approved + 1,
        pending: round.status === 'pending' ? prev.pending - 1 : prev.pending,
        disputed: round.status === 'disputed' ? prev.disputed - 1 : prev.disputed,
        approvalRate: Math.round(((prev.approved + 1) / prev.total) * 100)
      } : prev);

    } catch (err) {
      console.error('Error approving round:', err);
    }
  };

  const handleAdminDispute = async (round, reason) => {
    try {
      await updateDoc(doc(db, 'rounds', round.id), {
        status: 'disputed',
        disputeReason: reason,
        updatedAt: serverTimestamp(),
      });

      await createNotification(round.userId, 'score_disputed', { roundId: round.id });

      setSelectedRound(prev => ({ ...prev, status: 'disputed', disputeReason: reason }));
      setAllRounds(prev => prev.map(r =>
        r.id === round.id ? { ...r, status: 'disputed', disputeReason: reason } : r
      ));
      setRoundsList(prev => prev.map(r =>
        r.id === round.id ? { ...r, status: 'disputed', disputeReason: reason } : r
      ));
      setRoundStats(prev => prev ? {
        ...prev,
        disputed: prev.disputed + 1,
        pending: round.status === 'pending' ? prev.pending - 1 : prev.pending,
        approved: round.status === 'approved' ? prev.approved - 1 : prev.approved,
        approvalRate: Math.round(((prev.approved - (round.status === 'approved' ? 1 : 0)) / prev.total) * 100)
      } : prev);
      setShowDisputeForm(false);
      setDisputeInput('');

    } catch (err) {
      console.error('Error disputing round:', err);
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const seconds = Math.floor((Date.now() - timestamp.toDate()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const statusBadgeStyle = (status) => {
    const map = {
      approved: { backgroundColor: 'rgba(16,185,129,0.15)', color: '#10b981' },
      pending: { backgroundColor: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
      disputed: { backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444' },
    };
    return {
      ...(map[status] || { backgroundColor: 'rgba(156,163,175,0.15)', color: '#9ca3af' }),
      fontSize: '11px',
      fontWeight: '700',
      borderRadius: '4px',
      padding: '2px 8px',
      textTransform: 'uppercase',
    };
  };

  if (loading) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={styles.loadingState}>Loading analytics...</div>
        </div>
      </div>
    );
  }

  const totalDevices = stats.deviceBreakdown.mobile + stats.deviceBreakdown.desktop;
  const mobilePercent = totalDevices > 0 ? Math.round((stats.deviceBreakdown.mobile / totalDevices) * 100) : 0;
  const desktopPercent = 100 - mobilePercent;

  return (
    <div style={styles.overlay}>
      <style>{`
        @media (max-width: 640px) {
          .analytics-card-wide {
            max-height: 95vh !important;
          }
          .analytics-header {
            padding: 16px 18px !important;
          }
          .analytics-content {
            padding: 16px !important;
          }
          .analytics-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px !important;
          }
          .analytics-two-column {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          .analytics-player-row {
            grid-template-columns: 30px 1fr 72px !important;
            grid-template-rows: auto auto;
            gap: 8px !important;
          }
          .analytics-player-pages {
            grid-column: 1 / -1;
            justify-content: flex-start !important;
          }
          .analytics-player-last-active {
            font-size: 11px !important;
          }
          .analytics-dau-chart {
            height: 100px !important;
          }
          .analytics-round-pills {
            gap: 8px !important;
          }
          .modal-card {
            padding: 16px !important;
          }
          .round-detail-grid {
            grid-template-columns: 1fr !important;
          }
          .marker-row {
            grid-template-columns: 1fr auto !important;
            grid-template-rows: auto auto;
          }
        }
      `}</style>
      <div style={styles.cardWide} className="analytics-card-wide">
        {/* Header */}
        <div style={styles.header} className="analytics-header">
          <div>
            <h2 style={styles.title}>📊 Site Analytics</h2>
            <p style={styles.subtitle}>Real-time usage statistics</p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        {/* Content */}
        <div style={styles.content} className="analytics-content">
          {/* Overview Stats */}
          <div style={styles.statsGrid} className="analytics-stats-grid">
            <div style={styles.statCard}>
              <div style={styles.statIcon}>👥</div>
              <div style={styles.statValue}>{stats.onlineUsers.length}</div>
              <div style={styles.statLabel}>Online Now</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statIcon}>📈</div>
              <div style={styles.statValue}>{stats.activeToday}</div>
              <div style={styles.statLabel}>Active Today</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statIcon}>👁️</div>
              <div style={styles.statValue}>{stats.totalPageViews}</div>
              <div style={styles.statLabel}>Total Page Views</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statIcon}>⏱️</div>
              <div style={styles.statValue}>{stats.avgSessionDuration}m</div>
              <div style={styles.statLabel}>Avg Session</div>
            </div>
          </div>

          {/* Player Activity */}
          {stats.playerActivity.length > 0 && (
            <div style={styles.playerSection}>
              <h3 style={styles.sectionTitle}>👤 Player Activity</h3>
              <div style={styles.playerActivityList}>
                {stats.playerActivity.map((player, index) => (
                  <div key={player.userId} style={styles.playerActivityRow} className="analytics-player-row">
                    <div style={styles.playerActivityRank}>#{index + 1}</div>
                    <div style={styles.playerNameGroup}>
                      <div style={styles.playerActivityName}>{player.name}</div>
                      <div style={styles.playerActivityLastActive} className="analytics-player-last-active">
                        <span style={styles.playerActivityLastActiveLabel}>last seen </span>
                        {formatTimeAgo(player.lastActive)}
                      </div>
                    </div>
                    <div style={styles.playerActivityPages} className="analytics-player-pages">
                      {player.topPages.map(({ page, count }) => (
                        <span key={page} style={styles.playerPageBadge}>
                          {page} <strong style={styles.playerPageBadgeCount}>{count}</strong>
                        </span>
                      ))}
                    </div>
                    <div style={styles.playerActivityTotal}>{player.total} views</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 7-Day Daily Active Users Chart */}
          {dailyActiveUsers.length > 0 && (() => {
            const maxCount = Math.max(...dailyActiveUsers.map(d => d.count), 1);
            const todayStr = new Date().toDateString();
            return (
              <div style={styles.dauSection}>
                <div style={styles.dauHeader}>
                  <h3 style={styles.sectionTitle}>📅 Daily Active Users — Last 7 Days</h3>
                  <span style={styles.dauSubtitle}>{weeklyUniqueCount} unique this week</span>
                </div>
                <div style={styles.dauChart} className="analytics-dau-chart">
                  {dailyActiveUsers.map((day, i) => {
                    const barHeight = Math.max(4, (day.count / maxCount) * 80);
                    const isToday = day.date.toDateString() === todayStr;
                    return (
                      <div key={i} style={styles.dauBarCol}>
                        <div style={styles.dauCount}>{day.count}</div>
                        <div style={{
                          ...styles.dauBar,
                          height: barHeight,
                          backgroundColor: isToday ? '#10b981' : '#d4af37'
                        }} />
                        <div style={styles.dauLabel}>{day.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Round Submission Stats */}
          <div style={styles.roundSection}>
            <h3 style={styles.sectionTitle}>
              ⛳ Round Submissions{activeSeasonName ? ` — ${activeSeasonName}` : ''}
            </h3>
            {roundStats ? (
              <>
                <div style={styles.roundPills} className="analytics-round-pills">
                  {[
                    { label: 'Total', value: roundStats.total, color: '#9ca3af', valueColor: '#ffffff' },
                    { label: 'Approved', value: roundStats.approved, color: '#10b981', valueColor: '#10b981' },
                    { label: 'Pending', value: roundStats.pending, color: '#f59e0b', valueColor: '#f59e0b' },
                    { label: 'Disputed', value: roundStats.disputed, color: '#ef4444', valueColor: '#ef4444' },
                  ].map(({ label, value, color, valueColor }) => {
                    const statusKey = label === 'Total' ? 'all' : label.toLowerCase();
                    const isActive = roundsModalStatus === statusKey;
                    return (
                      <div
                        key={label}
                        style={{
                          ...styles.roundPill,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          border: `1px solid ${isActive ? color : '#2d2d2d'}`,
                          backgroundColor: isActive ? 'rgba(212,175,55,0.05)' : '#1e1e1e',
                        }}
                        onClick={() => {
                          const filtered = statusKey === 'all'
                            ? [...allRounds]
                            : allRounds.filter(r => r.status === statusKey);
                          filtered.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
                          setRoundsModalStatus(statusKey);
                          setRoundsList(filtered);
                        }}
                      >
                        <div style={{ ...styles.roundPillLabel, color }}>{label}</div>
                        <div style={{ ...styles.roundPillValue, color: valueColor }}>{value}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={styles.approvalRateRow}>
                  <span style={styles.approvalRateLabel}>Approval Rate</span>
                  <span style={styles.approvalRateValue}>{roundStats.approvalRate}%</span>
                </div>
                <div style={styles.approvalBarBg}>
                  <div style={{ ...styles.approvalBarFill, width: `${roundStats.approvalRate}%` }} />
                </div>
              </>
            ) : (
              <p style={styles.roundEmptyState}>No active season data available</p>
            )}
          </div>

          {/* Marker Performance */}
          <div style={styles.roundSection}>
            <h3 style={styles.sectionTitle}>👤 Marker Performance</h3>
            {markerStats.length === 0 ? (
              <p style={styles.roundEmptyState}>No marker data available yet</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {markerStats.map(marker => {
                  const hasPending = marker.pending > 0;
                  const avgHours = marker.avgApprovalHours !== null ? parseFloat(marker.avgApprovalHours) : null;
                  const timeColor = avgHours === null
                    ? '#6b7280'
                    : avgHours > 48
                      ? '#ef4444'
                      : avgHours <= 24
                        ? '#10b981'
                        : '#9ca3af';
                  return (
                    <div
                      key={marker.markerId}
                      className="marker-row"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto auto auto auto',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 14px',
                        borderRadius: '8px',
                        backgroundColor: hasPending ? 'rgba(245,158,11,0.05)' : '#1e1e1e',
                        borderLeft: hasPending ? '2px solid #f59e0b' : '2px solid transparent',
                        border: hasPending
                          ? '1px solid #2d2d2d'
                          : '1px solid #2d2d2d',
                        borderLeftWidth: hasPending ? '2px' : '1px',
                        borderLeftColor: hasPending ? '#f59e0b' : '#2d2d2d',
                      }}
                    >
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff' }}>
                        {marker.name}
                      </div>
                      <div style={{
                        backgroundColor: 'rgba(16,185,129,0.15)', color: '#10b981',
                        fontSize: '11px', fontWeight: '700', borderRadius: '4px',
                        padding: '2px 8px', textTransform: 'uppercase', whiteSpace: 'nowrap'
                      }}>
                        {marker.approved} approved
                      </div>
                      <div style={{
                        backgroundColor: hasPending ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.08)',
                        color: '#f59e0b',
                        fontSize: '11px', fontWeight: '700', borderRadius: '4px',
                        padding: '2px 8px', textTransform: 'uppercase', whiteSpace: 'nowrap'
                      }}>
                        {hasPending ? '⚠️ ' : ''}{marker.pending} pending
                      </div>
                      <div style={{
                        backgroundColor: marker.disputed > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)',
                        color: '#ef4444',
                        fontSize: '11px', fontWeight: '700', borderRadius: '4px',
                        padding: '2px 8px', textTransform: 'uppercase', whiteSpace: 'nowrap'
                      }}>
                        {marker.disputed} disputed
                      </div>
                      <div style={{ fontSize: '12px', color: timeColor, whiteSpace: 'nowrap', minWidth: '60px', textAlign: 'right' }}>
                        {avgHours !== null ? `${marker.avgApprovalHours}h avg` : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Two Column Layout */}
          <div style={styles.twoColumn} className="analytics-two-column">
            {/* Left Column */}
            <div style={styles.column}>
              {/* Online Users */}
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>🟢 Online Now ({stats.onlineUsers.length})</h3>
                {stats.onlineUsers.length === 0 ? (
                  <p style={styles.emptyState}>No users online</p>
                ) : (
                  <div style={styles.usersList}>
                    {stats.onlineUsers.map((user) => (
                      <div key={user.id} style={styles.userCard}>
                        <div style={styles.userInfo}>
                          <div style={styles.onlineDot}></div>
                          <div>
                            <div style={styles.userId}>{user.userId}</div>
                            <div style={styles.userPage}>{user.currentPage || 'Unknown page'}</div>
                          </div>
                        </div>
                        <div style={styles.userTime}>{formatTimeAgo(user.lastActive)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Device Breakdown */}
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>📱 Device Breakdown</h3>
                <div style={styles.deviceBar}>
                  <div style={{...styles.deviceSegment, width: `${mobilePercent}%`, backgroundColor: '#3b82f6'}}>
                    {mobilePercent > 15 && <span style={styles.deviceLabel}>📱 {mobilePercent}%</span>}
                  </div>
                  <div style={{...styles.deviceSegment, width: `${desktopPercent}%`, backgroundColor: '#d4af37'}}>
                    {desktopPercent > 15 && <span style={styles.deviceLabel}>💻 {desktopPercent}%</span>}
                  </div>
                </div>
                <div style={styles.deviceLegend}>
                  <div><span style={{...styles.legendDot, backgroundColor: '#3b82f6'}}></span> Mobile: {stats.deviceBreakdown.mobile}</div>
                  <div><span style={{...styles.legendDot, backgroundColor: '#d4af37'}}></span> Desktop: {stats.deviceBreakdown.desktop}</div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div style={styles.column}>
              {/* Popular Pages */}
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>🔥 Most Popular Pages</h3>
                <div style={styles.pagesList}>
                  {stats.popularPages.slice(0, 8).map((item, index) => {
                    const maxCount = stats.popularPages[0]?.count || 1;
                    const barWidth = (item.count / maxCount) * 100;

                    return (
                      <div key={index} style={styles.pageItem}>
                        <div style={styles.pageInfo}>
                          <span style={styles.pageRank}>#{index + 1}</span>
                          <span style={styles.pageName}>{item.page}</span>
                          <span style={styles.pageCount}>{item.count}</span>
                        </div>
                        <div style={styles.pageBarBg}>
                          <div style={{...styles.pageBarFill, width: `${barWidth}%`}}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent Activity */}
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>⚡ Recent Activity</h3>
                <div style={styles.activityList}>
                  {stats.recentActivity.slice(0, 10).map((activity) => (
                    <div key={activity.id} style={styles.activityItem}>
                      <div style={styles.activityDot}></div>
                      <div style={styles.activityInfo}>
                        <div style={styles.activityPage}>{activity.pageName}</div>
                        <div style={styles.activityTime}>{formatTimeAgo(activity.timestamp)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rounds List Modal */}
      {roundsModalStatus !== null && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 3000, display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: '20px',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setRoundsModalStatus(null);
              setSelectedRound(null);
            }
          }}
        >
          <div style={{
            backgroundColor: '#1e1e1e', borderRadius: '12px',
            border: '1px solid #2d2d2d', width: '100%', maxWidth: '700px',
            maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '20px 24px', borderBottom: '1px solid #2d2d2d', flexShrink: 0,
            }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#ffffff' }}>
                {roundsList.length}{' '}
                {roundsModalStatus === 'all'
                  ? 'Total'
                  : roundsModalStatus.charAt(0).toUpperCase() + roundsModalStatus.slice(1)}{' '}
                Rounds
              </div>
              <button
                onClick={() => { setRoundsModalStatus(null); setSelectedRound(null); }}
                style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '20px', cursor: 'pointer', padding: '4px 8px' }}
              >
                ✕
              </button>
            </div>

            {/* Round List */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
              {roundsList.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px 20px', fontSize: '14px' }}>
                  No rounds found
                </div>
              ) : (
                roundsList.map(round => (
                  <div
                    key={round.id}
                    onClick={() => setSelectedRound(round)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '80px 1fr 1fr auto 24px',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 24px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #2d2d2d',
                      transition: 'background-color 0.15s ease',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1a1a1a'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                      {formatDate(round.date)}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff', letterSpacing: '-0.01em' }}>
                      {users[round.userId]?.name || 'Unknown'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                      👤 {users[round.markerId]?.name || 'Unknown'}
                    </div>
                    <div style={statusBadgeStyle(round.status)}>
                      {round.status}
                    </div>
                    <div style={{ color: '#d4af37', fontSize: '16px' }}>→</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Round Detail Modal */}
      {selectedRound !== null && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 3100, display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: '20px',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedRound(null);
          }}
        >
          <div style={{
            backgroundColor: '#1e1e1e', borderRadius: '12px',
            border: '1px solid #2d2d2d', width: '100%', maxWidth: '500px',
            maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          }}>
            {/* Detail Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '20px 24px', borderBottom: '1px solid #2d2d2d', flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  onClick={() => setSelectedRound(null)}
                  style={{ background: 'none', border: 'none', color: '#d4af37', fontSize: '14px', cursor: 'pointer', fontWeight: '600', padding: 0 }}
                >
                  ← Back
                </button>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#ffffff' }}>Round Detail</div>
              </div>
              <button
                onClick={() => { setRoundsModalStatus(null); setSelectedRound(null); }}
                style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '20px', cursor: 'pointer', padding: '4px 8px' }}
              >
                ✕
              </button>
            </div>

            {/* Detail Content */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Player & Marker cards */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ backgroundColor: '#0f0f0f', borderRadius: '10px', border: '1px solid #2d2d2d', padding: '14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                    🏌️ Player
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#d4af37', letterSpacing: '-0.01em', marginBottom: '4px' }}>
                    {users[selectedRound.userId]?.name || 'Unknown'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                    Handicap {selectedRound.courseHandicap}
                  </div>
                </div>
                <div style={{ backgroundColor: '#0f0f0f', borderRadius: '10px', border: '1px solid #2d2d2d', padding: '14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                    👤 Marker
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#ffffff', letterSpacing: '-0.01em' }}>
                    {users[selectedRound.markerId]?.name || 'Unknown'}
                  </div>
                </div>
              </div>

              {/* Round details grid */}
              <div
                className="round-detail-grid"
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}
              >
                {[
                  { label: 'Date', value: selectedRound.date ? new Date(selectedRound.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—' },
                  { label: 'Event', value: selectedRound.eventName || '—' },
                  { label: 'Course', value: selectedRound.course || '—' },
                  { label: 'Handicap', value: selectedRound.courseHandicap ?? '—' },
                ].map(({ label, value }) => (
                  <div key={label} style={{ backgroundColor: '#0f0f0f', borderRadius: '8px', border: '1px solid #2d2d2d', padding: '12px' }}>
                    <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{label}</div>
                    <div style={{ fontSize: '14px', color: '#ffffff', fontWeight: '600' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Score summary */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1, backgroundColor: '#0f0f0f', borderRadius: '8px', border: '1px solid #2d2d2d', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Stableford</div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#d4af37' }}>{selectedRound.totalStableford ?? '—'}</div>
                </div>
                <div style={{ flex: 1, backgroundColor: '#0f0f0f', borderRadius: '8px', border: '1px solid #2d2d2d', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Gross</div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#9ca3af' }}>{selectedRound.totalGross ?? '—'}</div>
                </div>
                <div style={{ flex: 1, backgroundColor: '#0f0f0f', borderRadius: '8px', border: '1px solid #2d2d2d', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Putts</div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#3b82f6' }}>{selectedRound.totalPutts ?? '—'}</div>
                </div>
              </div>

              {/* Status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ fontSize: '13px', color: '#9ca3af', fontWeight: '500' }}>Status:</div>
                <div style={statusBadgeStyle(selectedRound.status)}>{selectedRound.status}</div>
              </div>

              {/* Dispute reason */}
              {selectedRound.status === 'disputed' && selectedRound.disputeReason && (
                <div style={{
                  backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#ef4444',
                }}>
                  ⚠️ Dispute Reason: {selectedRound.disputeReason}
                </div>
              )}

              {/* Admin action buttons */}
              {(selectedRound.status === 'pending' || selectedRound.status === 'disputed') && !showDisputeForm && (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleAdminApprove(selectedRound)}
                    style={{
                      flex: 1, backgroundColor: '#10b981', color: '#0f0f0f',
                      fontWeight: '700', borderRadius: '8px', padding: '12px',
                      border: 'none', cursor: 'pointer',
                      fontSize: '15px', transition: 'opacity 0.2s ease',
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    ✅ Approve
                  </button>
                  <button
                    onClick={() => setShowDisputeForm(true)}
                    style={{
                      flex: 1, backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444',
                      fontWeight: '700', borderRadius: '8px', padding: '12px',
                      border: '1px solid rgba(239,68,68,0.3)', cursor: 'pointer',
                      fontSize: '15px', transition: 'opacity 0.2s ease',
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    ⚠️ Dispute
                  </button>
                </div>
              )}
              {(selectedRound.status === 'pending' || selectedRound.status === 'disputed') && showDisputeForm && (
                <div style={{
                  backgroundColor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px',
                }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#ef4444' }}>Dispute reason</div>
                  <textarea
                    value={disputeInput}
                    onChange={e => setDisputeInput(e.target.value)}
                    placeholder="Explain why this round is being disputed…"
                    rows={3}
                    style={{
                      width: '100%', backgroundColor: '#0f0f0f', border: '1px solid rgba(239,68,68,0.3)',
                      borderRadius: '6px', padding: '10px', color: '#ffffff', fontSize: '14px',
                      resize: 'vertical', fontFamily: 'Inter, sans-serif', boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleAdminDispute(selectedRound, disputeInput.trim())}
                      disabled={!disputeInput.trim()}
                      style={{
                        flex: 1, backgroundColor: disputeInput.trim() ? '#ef4444' : 'rgba(239,68,68,0.3)',
                        color: '#ffffff', fontWeight: '700', borderRadius: '6px', padding: '10px',
                        border: 'none', cursor: disputeInput.trim() ? 'pointer' : 'not-allowed',
                        fontSize: '14px', transition: 'opacity 0.2s ease',
                      }}
                    >
                      Confirm Dispute
                    </button>
                    <button
                      onClick={() => { setShowDisputeForm(false); setDisputeInput(''); }}
                      style={{
                        flex: 1, backgroundColor: '#1a1a1a', color: '#9ca3af',
                        fontWeight: '600', borderRadius: '6px', padding: '10px',
                        border: '1px solid #2d2d2d', cursor: 'pointer', fontSize: '14px',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    overflowY: 'auto',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  card: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.25s ease'
  },
  cardWide: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.25s ease'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 30px',
    borderBottom: '1px solid #2d2d2d',
    flexShrink: 0
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#ffffff',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  subtitle: {
    fontSize: '14px',
    color: '#9ca3af',
    margin: '5px 0 0 0',
    fontWeight: '400'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '5px 10px',
    transition: 'all 0.3s ease'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '30px'
  },
  loadingState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#6b7280',
    fontSize: '16px',
    fontWeight: '400'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  },
  statCard: {
    backgroundColor: '#0f0f0f',
    padding: '24px',
    borderRadius: '12px',
    textAlign: 'center',
    border: '1px solid #2d2d2d'
  },
  statIcon: {
    fontSize: '32px',
    marginBottom: '12px'
  },
  statValue: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#d4af37',
    marginBottom: '8px',
    letterSpacing: '-0.02em'
  },
  statLabel: {
    fontSize: '13px',
    color: '#6b7280',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  twoColumn: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '30px'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px'
  },
  section: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '12px',
    border: '1px solid #2d2d2d'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    marginTop: 0,
    marginBottom: '16px',
    letterSpacing: '-0.01em'
  },
  emptyState: {
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '14px',
    padding: '20px',
    fontWeight: '400'
  },
  usersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  userCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#1e1e1e',
    borderRadius: '8px',
    border: '1px solid #2d2d2d'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  onlineDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#10b981',
    flexShrink: 0,
    boxShadow: '0 0 8px rgba(16, 185, 129, 0.5)'
  },
  userId: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    letterSpacing: '-0.01em'
  },
  userPage: {
    fontSize: '12px',
    color: '#9ca3af',
    fontWeight: '400'
  },
  userTime: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500'
  },
  deviceBar: {
    display: 'flex',
    height: '40px',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '12px'
  },
  deviceSegment: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'width 0.5s ease'
  },
  deviceLabel: {
    color: '#0f0f0f',
    fontSize: '14px',
    fontWeight: '700'
  },
  deviceLegend: {
    display: 'flex',
    justifyContent: 'space-around',
    fontSize: '13px',
    color: '#9ca3af',
    fontWeight: '500'
  },
  legendDot: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    marginRight: '6px'
  },
  pagesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  pageItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  pageInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  pageRank: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#6b7280',
    width: '30px'
  },
  pageName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    flex: 1,
    letterSpacing: '-0.01em'
  },
  pageCount: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#d4af37'
  },
  pageBarBg: {
    height: '6px',
    backgroundColor: '#2d2d2d',
    borderRadius: '3px',
    overflow: 'hidden'
  },
  pageBarFill: {
    height: '100%',
    backgroundColor: '#d4af37',
    transition: 'width 0.5s ease'
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '300px',
    overflowY: 'auto'
  },
  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px',
    backgroundColor: '#1e1e1e',
    borderRadius: '6px'
  },
  activityDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#d4af37',
    flexShrink: 0
  },
  activityInfo: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  activityPage: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#ffffff'
  },
  activityTime: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '400'
  },
  playerSection: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '12px',
    border: '1px solid #2d2d2d',
    marginBottom: '30px'
  },
  playerActivityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  playerActivityRow: {
    display: 'grid',
    gridTemplateColumns: '36px 1fr auto 80px',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 14px',
    backgroundColor: '#1e1e1e',
    borderRadius: '8px',
    border: '1px solid #2d2d2d'
  },
  playerNameGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  playerActivityRank: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#6b7280'
  },
  playerActivityName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    letterSpacing: '-0.01em'
  },
  playerActivityPages: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },
  playerPageBadge: {
    fontSize: '11px',
    color: '#9ca3af',
    backgroundColor: '#0f0f0f',
    border: '1px solid #2d2d2d',
    borderRadius: '4px',
    padding: '2px 8px',
    whiteSpace: 'nowrap',
    fontWeight: '500'
  },
  playerPageBadgeCount: {
    color: '#d4af37',
    fontWeight: '700'
  },
  playerActivityLastActive: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500'
  },
  playerActivityLastActiveLabel: {
    fontSize: '10px',
    color: '#4b5563',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  playerActivityTotal: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#d4af37',
    textAlign: 'right',
    whiteSpace: 'nowrap'
  },
  dauSection: {
    backgroundColor: '#0f0f0f',
    border: '1px solid #2d2d2d',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '30px',
    maxWidth: '100%',
    boxSizing: 'border-box'
  },
  dauHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '16px'
  },
  dauSubtitle: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500'
  },
  dauChart: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    height: '120px',
    width: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box'
  },
  dauBarCol: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  dauCount: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '2px'
  },
  dauBar: {
    width: '100%',
    minHeight: '4px',
    borderRadius: '4px 4px 0 0'
  },
  dauLabel: {
    fontSize: '11px',
    color: '#6b7280',
    marginTop: '6px',
    textAlign: 'center'
  },
  roundSection: {
    backgroundColor: '#0f0f0f',
    border: '1px solid #2d2d2d',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '30px',
    maxWidth: '100%',
    boxSizing: 'border-box'
  },
  roundPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '16px'
  },
  roundPill: {
    backgroundColor: '#1e1e1e',
    border: '1px solid #2d2d2d',
    borderRadius: '10px',
    padding: '14px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: '1 1 120px',
    boxSizing: 'border-box'
  },
  roundPillLabel: {
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  roundPillValue: {
    fontSize: '28px',
    fontWeight: '700',
    letterSpacing: '-0.02em'
  },
  approvalRateRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px'
  },
  approvalRateLabel: {
    fontSize: '13px',
    color: '#9ca3af',
    fontWeight: '500'
  },
  approvalRateValue: {
    fontSize: '13px',
    color: '#10b981',
    fontWeight: '700'
  },
  approvalBarBg: {
    width: '100%',
    height: '8px',
    backgroundColor: '#2d2d2d',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  approvalBarFill: {
    height: '100%',
    backgroundColor: '#10b981',
    borderRadius: '4px',
    transition: 'width 0.8s ease'
  },
  roundEmptyState: {
    fontSize: '14px',
    color: '#6b7280',
    textAlign: 'center',
    padding: '20px',
    margin: 0
  }
};

export default SiteAnalytics;
